import { Injectable } from "@angular/core";
import { LogEntry } from "./log.service";
import { scrubLogText } from "./log-scrubber";

export type ClassifyAction =
  | "upload-known-bug"
  | "upload-unknown"
  | "skip-noise"
  | "skip-benign"
  | "skip-duplicate"
  | "skip-session-cap";

export interface ClassificationResult {
  /** Whether the buffer should actually be uploaded. */
  upload: boolean;
  action: ClassifyAction;
  /** Pre-computed category when a known pattern matched (sent for telemetry). */
  category?: string;
  severity?: "low" | "medium" | "high";
  /** Normalized fingerprint of the triggering error (dedup key). */
  signature?: string;
}

/**
 * On-device log classifier. Runs BEFORE any upload to drastically cut the
 * volume of diagnostic traffic — the device decides whether a buffer is
 * worth sending at all, instead of letting the backend receive everything.
 *
 * Three volume levers:
 *   1. skip-noise    — buffer has no real error.
 *   2. skip-benign   — the error matches a known-harmless pattern
 *                      (AbortError on navigation, autoplay blocked,
 *                      ResizeObserver loop, etc.).
 *   3. skip-duplicate — the same error signature was already uploaded
 *                       within DEDUP_TTL. The same bug firing 100× in a
 *                       session is uploaded ONCE.
 *   + a hard per-session cap as a final safety net.
 *
 * Anything that passes is tagged `upload-known-bug` (with a pre-filled
 * category, mirroring the backend rule engine) or `upload-unknown` (needs
 * server-side LLM triage).
 *
 * NOTE: this only gates AUTO uploads. Manual "Envoyer au support" sends
 * bypass the classifier entirely — the user explicitly asked.
 */
@Injectable({ providedIn: "root" })
export class LogClassifierService {
  private static readonly DEDUP_KEY = "diag_uploaded_signatures";
  /** A given error signature is re-uploadable only once per this window. */
  private static readonly DEDUP_TTL_MS = 6 * 60 * 60 * 1000; // 6h
  /** Hard cap of auto-upload attempts per app session, including failures. */
  private static readonly SESSION_CAP = 12;

  private sessionAttemptCount = 0;
  private dedupCache: Record<string, number> | null = null;

  /** Known-harmless "errors" — never worth an upload. */
  private static readonly BENIGN: RegExp[] = [
    /\bAbortError\b/i,                                  // fetch aborted on navigation
    /The (play\(\) )?request was interrupted/i,         // <audio>/<video> play interrupted
    /\bNotAllowedError\b/i,                             // autoplay blocked
    /ResizeObserver loop (limit exceeded|completed)/i,  // famous benign browser noise
    /Non-Error promise rejection captured/i,
    /Script error\.?$/i,                                // opaque cross-origin script error
    /\bcancelled\b/i,                                   // generic user-cancelled flows
  ];

  /**
   * Known bug patterns (mirror of the backend ClientLogRuleEngine). Ordered
   * specific-first; first match wins. Generic JS catch-alls live at the end.
   */
  private static readonly KNOWN: Array<{
    category: string;
    severity: "low" | "medium" | "high";
    re: RegExp;
  }> = [
    { category: "auth/expired", severity: "high", re: /(401\s+Unauthorized|jwt expired|Token has expired|AuthError.*403)/i },
    { category: "pusher/auth", severity: "high", re: /(Pusher.*error|subscription_error|Error subscribing to (role|discussion) channel)/i },
    { category: "network/offline", severity: "medium", re: /(NetworkError|Failed to fetch|ERR_INTERNET_DISCONNECTED|ENETUNREACH)/i },
    { category: "network/timeout", severity: "medium", re: /(ETIMEDOUT|ERR_TIMED_OUT|Request timeout|timeout of \d+ms)/i },
    { category: "map/route-failed", severity: "medium", re: /(No routes found|No route returned by MapKit|getRouteGeometry.*failed)/i },
    { category: "angular/view-destroyed", severity: "medium", re: /(ViewDestroyedError|Attempt to use a destroyed view)/i },
    { category: "js/null-access", severity: "medium", re: /(Cannot read propert(ies|y) of (undefined|null)|undefined is not an object)/i },
    { category: "js/uncaught", severity: "high", re: /(\[window\.error\]|\[unhandledrejection\])/i },
  ];

  /** Call at the start of each app session (AppComponent boot). */
  resetSession(): void {
    this.sessionAttemptCount = 0;
    this.dedupCache = this.loadDedupMapFromStorage();
  }

  /**
   * Decide whether the current buffer is worth an auto-upload.
   * Looks at the most recent error entry.
   */
  classify(entries: LogEntry[]): ClassificationResult {
    const lastError = this.lastErrorEntry(entries);
    if (!lastError) {
      return { upload: false, action: "skip-noise" };
    }

    const message = scrubLogText(lastError.message || "");
    if (!message.trim()) {
      return { upload: false, action: "skip-noise" };
    }

    // 1. Benign? Never upload.
    if (LogClassifierService.BENIGN.some((re) => re.test(message))) {
      return { upload: false, action: "skip-benign" };
    }

    const signature = this.computeSignature(message);

    // 2. Already uploaded recently? Skip the duplicate.
    if (this.wasRecentlyUploaded(signature)) {
      return { upload: false, action: "skip-duplicate", signature };
    }

    // 3. Session cap reached? Stop — safety net against runaway loops.
    if (this.sessionAttemptCount >= LogClassifierService.SESSION_CAP) {
      return { upload: false, action: "skip-session-cap", signature };
    }

    // 4. Known bug pattern → upload with a pre-filled category.
    for (const rule of LogClassifierService.KNOWN) {
      if (rule.re.test(message)) {
        return {
          upload: true,
          action: "upload-known-bug",
          category: rule.category,
          severity: rule.severity,
          signature,
        };
      }
    }

    // 5. Unknown error → upload for server-side LLM triage.
    return { upload: true, action: "upload-unknown", signature };
  }

  /**
   * Record a signature as uploaded so subsequent identical errors are
   * deduped for DEDUP_TTL_MS. Call AFTER a successful upload.
   */
  recordUploaded(signature: string | undefined): void {
    if (!signature) return;
    const map = this.getDedupMap();
    map[signature] = Date.now();
    this.persistDedupMap(this.prune(map));
  }

  recordUploadedMany(signatures: Iterable<string | undefined>): void {
    const now = Date.now();
    const map = this.getDedupMap();
    let changed = false;
    for (const signature of signatures) {
      if (!signature) continue;
      map[signature] = now;
      changed = true;
    }
    if (changed) {
      this.persistDedupMap(this.prune(map));
    }
  }

  /** Count attempted auto-uploads, including failures, to cap offline loops. */
  recordUploadAttempt(): void {
    this.sessionAttemptCount++;
  }

  /** Latest entry with level === "error", or null. */
  private lastErrorEntry(entries: LogEntry[]): LogEntry | null {
    for (let i = entries.length - 1; i >= 0; i--) {
      if (entries[i]?.level === "error") return entries[i];
    }
    return null;
  }

  /**
   * Normalize an error message into a stable fingerprint: first line, with
   * volatile parts (quoted values, numbers, hex, URLs) replaced by tokens,
   * plus the first stack frame's function name. Two occurrences of the
   * "same" bug collapse to the same signature.
   */
  private computeSignature(message: string): string {
    const scrubbed = scrubLogText(message);
    const firstLine = (scrubbed.split("\n")[0] || "").slice(0, 300);
    const normalized = firstLine
      .replace(/'[^']*'/g, "'<>'")
      .replace(/"[^"]*"/g, '"<>"')
      .replace(/0x[0-9a-fA-F]+/g, "<hex>")
      .replace(/https?:\/\/\S+/g, "<url>")
      .replace(/\b\d+\b/g, "<n>")
      .trim();
    const frameMatch = scrubbed.match(/\bat\s+([A-Za-z0-9_$.]+)/);
    const frame = frameMatch ? frameMatch[1] : "";
    return `${normalized}|${frame}`.slice(0, 200);
  }

  private wasRecentlyUploaded(signature: string): boolean {
    const map = this.getDedupMap();
    const ts = map[signature];
    return (
      typeof ts === "number" &&
      Date.now() - ts < LogClassifierService.DEDUP_TTL_MS
    );
  }

  private getDedupMap(): Record<string, number> {
    if (this.dedupCache === null) {
      this.dedupCache = this.loadDedupMapFromStorage();
    }
    return this.dedupCache;
  }

  private loadDedupMapFromStorage(): Record<string, number> {
    try {
      const raw = localStorage.getItem(LogClassifierService.DEDUP_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  private persistDedupMap(map: Record<string, number>): void {
    this.dedupCache = map;
    try {
      localStorage.setItem(LogClassifierService.DEDUP_KEY, JSON.stringify(map));
    } catch (_) { }
  }

  /** Drop entries older than the TTL so the map can't grow unbounded. */
  private prune(map: Record<string, number>): Record<string, number> {
    const now = Date.now();
    const out: Record<string, number> = {};
    for (const [sig, ts] of Object.entries(map)) {
      if (typeof ts === "number" && now - ts < LogClassifierService.DEDUP_TTL_MS) {
        out[sig] = ts;
      }
    }
    return out;
  }
}
